import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, Plus, Trash2, Clock, Loader2, X } from "lucide-react";

export default function AdminCapacitacion() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", duration_min: 30, required: true });

  const load = async () => {
    try {
      const { data } = await api.get("/training/courses");
      setCourses(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Escribe un título."); return; }
    setSaving(true);
    try {
      await api.post("/training/courses", { ...form, duration_min: Number(form.duration_min) || 30 });
      toast.success("Curso creado.");
      setOpen(false);
      setForm({ title: "", description: "", duration_min: 30, required: true });
      await load();
    } catch {
      toast.error("No se pudo crear el curso.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/training/courses/${id}`);
      toast.success("Curso eliminado.");
      await load();
    } catch {
      toast.error("No se pudo eliminar.");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-capacitacion-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#2C49A0]">Capacitación Docente</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] flex items-center gap-2">
            <GraduationCap className="text-[#2C49A0]" /> Cursos y certificación
          </h1>
          <p className="text-sm text-[#6E7591] mt-1">Crea los cursos de formación. Los docentes se certifican al completar los requeridos.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="open-create-course"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#1F2547] text-white font-bold text-sm hover:bg-[#2C49A0] transition-colors">
          <Plus size={18} /> Nuevo curso
        </button>
      </div>

      {loading ? (
        <div className="jasq-card p-10 text-center text-sm text-[#6E7591]">Cargando…</div>
      ) : courses.length === 0 ? (
        <div className="jasq-card p-10 text-center text-sm text-[#6E7591]" data-testid="capacitacion-empty">Aún no hay cursos. Crea el primero.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="capacitacion-grid">
          {courses.map((c) => (
            <div key={c.id} className="jasq-card p-5 flex items-start gap-3" data-testid={`course-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-extrabold text-[#1F2547]">{c.title}</h3>
                  {c.required && <span className="text-[9px] font-bold uppercase bg-[#FDEBF4] text-[#D63384] rounded px-1.5 py-0.5">Requerido</span>}
                </div>
                <p className="text-sm text-[#6E7591] mt-0.5">{c.description || "Curso de formación interna."}</p>
                <p className="text-xs text-[#A6ACC1] mt-1 flex items-center gap-1"><Clock size={12} /> {c.duration_min} min</p>
              </div>
              <button onClick={() => remove(c.id)} data-testid={`delete-course-${c.id}`}
                className="w-9 h-9 rounded-lg grid place-items-center text-[#D63384] hover:bg-[#FDEBF4] shrink-0" title="Eliminar">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[80] bg-[#1F2547]/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="create-course-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-extrabold text-[#1F2547]">Nuevo curso</h2>
              <button onClick={() => setOpen(false)} className="text-[#6E7591]"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="course-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Título del curso" className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E1E5F0] text-sm focus:border-[#2C49A0] outline-none" />
              <textarea data-testid="course-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Descripción" rows={3} className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E1E5F0] text-sm focus:border-[#2C49A0] outline-none" />
              <div className="flex items-center gap-3">
                <label className="text-sm text-[#4C5478]">Duración (min)</label>
                <input data-testid="course-duration" type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                  className="w-24 px-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-sm focus:border-[#2C49A0] outline-none" />
                <label className="ml-auto flex items-center gap-2 text-sm text-[#4C5478]">
                  <input data-testid="course-required" type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} />
                  Requerido
                </label>
              </div>
            </div>
            <button onClick={create} disabled={saving} data-testid="save-course"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-[#1F2547] text-white font-bold text-sm hover:bg-[#2C49A0] transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />} Crear curso
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
